import { useEffect, useRef, useState } from "react";

import { getJob } from "../api/jobs";
import { HttpError } from "../api/client";
import { TERMINAL_STATUSES } from "../api/types";
import type { JobStatusResponse } from "../api/types";

export interface UseJobPolling {
  data: JobStatusResponse | null;
  error: string | null;
  isPolling: boolean;
}

// After seeing a terminal status, fire one more poll this many milliseconds
// later before tearing the loop down. Belt-and-suspenders against a stale
// Celery chain that re-runs after `mark_job_completed` was called too early
// (see `runner_impl._is_last_in_chain`). If the reconcile poll reports a
// non-terminal status, polling resumes; otherwise we stop for good.
const RECONCILE_DELAY_MS = 3000;

/**
 * Poll GET /api/jobs/{id} until the job reaches a terminal state.
 *
 * - Interval starts at 1500ms and doubles every 6 polls up to 8000ms cap.
 * - On terminal status, schedules a single reconcile poll ~3s later. If the
 *   reconcile poll still reports a terminal status, polling stops; if it
 *   reports a non-terminal value (a stale-chain regression), polling
 *   resumes at the cap.
 * - Aborts on unmount or when jobId changes.
 */
export function useJobPolling(
  jobId: string | null,
  fallbackError = "İş durumu sorgulanamadı",
): UseJobPolling {
  const [data, setData] = useState<JobStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState<boolean>(jobId !== null);
  const [previousJobId, setPreviousJobId] = useState<string | null>(jobId);
  const cancelledRef = useRef(false);
  const fallbackErrorRef = useRef(fallbackError);

  // Render-time state adjustment (see https://react.dev/learn/you-might-not-need-an-effect)
  // — when jobId changes, reset the response state synchronously so the next
  // commit observes the cleared values without waiting for the polling effect
  // to fire.
  if (jobId !== previousJobId) {
    setPreviousJobId(jobId);
    setData(null);
    setError(null);
    setIsPolling(jobId !== null);
  }

  useEffect(() => {
    fallbackErrorRef.current = fallbackError;
  }, [fallbackError]);

  useEffect(() => {
    cancelledRef.current = false;
    if (!jobId) {
      return;
    }

    let interval = 1500;
    let pollCount = 0;
    let reconcileFired = false;
    const timers: Array<ReturnType<typeof setTimeout>> = [];

    const scheduleNext = (delay: number): void => {
      const handle = setTimeout(tick, delay);
      timers.push(handle);
    };

    const tick = async (): Promise<void> => {
      /* v8 ignore next -- defensive: cleanup clears timers so this guard cannot fire synchronously */
      if (cancelledRef.current) return;
      try {
        const status = await getJob(jobId);
        if (cancelledRef.current) return;
        setData(status);
        if (TERMINAL_STATUSES.has(status.status)) {
          if (!reconcileFired) {
            // First terminal observation: leave isPolling true while we
            // wait one more tick to catch a stale-chain regression.
            reconcileFired = true;
            scheduleNext(RECONCILE_DELAY_MS);
            return;
          }
          // Reconcile poll also terminal — stop for good.
          setIsPolling(false);
          return;
        }
        // Non-terminal: if a reconcile previously fired and we now see a
        // running status again, resume normal polling.
        reconcileFired = false;
        setIsPolling(true);
      } catch (err) {
        if (cancelledRef.current) return;
        const message =
          err instanceof HttpError
            ? err.toUserMessage()
            : err instanceof Error
              ? err.message
              : fallbackErrorRef.current;
        setError(message);
        setIsPolling(false);
        return;
      }
      pollCount += 1;
      if (pollCount % 6 === 0) {
        interval = Math.min(interval * 2, 8000);
      }
      scheduleNext(interval);
    };

    void tick();

    return () => {
      cancelledRef.current = true;
      for (const t of timers) clearTimeout(t);
    };
  }, [jobId]);

  return { data, error, isPolling };
}
